import { ComponentType, ReactElement, ReactNode } from 'react';

import { isNullish } from '@utils/isNullish';

type ElementType = string | symbol | ComponentType<unknown>;

const isReactElement = (
  node: ReactNode
): node is ReactElement<{ children?: ReactNode }> => {
  return typeof node === 'object' && node !== null && 'type' in node;
};

const primitiveToString = (node: ReactNode): string => {
  return typeof node === 'string' || typeof node === 'number'
    ? String(node)
    : '';
};

/**
 * Extracts plain text content ignoring all HTML formatting
 */
const extractTextContent = (node: ReactNode): string => {
  if (isNullish(node)) return '';

  const primitiveContent = primitiveToString(node);
  if (primitiveContent) return primitiveContent;

  if (Array.isArray(node)) {
    return node.map(extractTextContent).join('');
  }

  if (isReactElement(node)) {
    return extractTextContent(node.props.children);
  }

  return '';
};

// Formatting tags are <b> and <a>
// Helper function to check if a component is a BundleBot component
const isBundleBotComponent = (type: ElementType): boolean => {
  if (typeof type === 'function') {
    const name = type.name;
    return (
      name === 'BundleBotHeading' ||
      name === 'BundleBotText' ||
      name === 'BundleBotBold' ||
      name === 'BundleBotLink'
    );
  }
  return false;
};

// Helper function to get the equivalent HTML tag for BundleBot components
const getBundleBotHtmlEquivalent = (type: ElementType): string => {
  if (typeof type === 'function') {
    const name = type.name;
    switch (name) {
      case 'BundleBotHeading':
        return 'h4';
      case 'BundleBotText':
        return 'p';
      case 'BundleBotBold':
        return 'b';
      case 'BundleBotLink':
        return 'a';
    }
  }
  return '';
};

const handleFormattingTag = (
  type: ElementType,
  children: ReactNode
): string => {
  const textContent = extractTextContent(children);

  // Handle BundleBot components
  if (isBundleBotComponent(type)) {
    const htmlTag = getBundleBotHtmlEquivalent(type);
    return htmlTag === 'b'
      ? `<b>${textContent}</b>`
      : htmlTag === 'a'
        ? `<a>${textContent}</a>`
        : textContent;
  }

  // Handle regular HTML tags
  return type === 'b'
    ? `<b>${textContent}</b>`
    : type === 'a'
      ? `<a>${textContent}</a>`
      : textContent;
};

const processChildNode = (child: ReactNode): string => {
  if (isNullish(child)) return '';

  const primitiveContent = primitiveToString(child);
  if (primitiveContent) return primitiveContent;

  if (isReactElement(child)) {
    const { type, props } = child;

    // Handle BundleBot formatting components
    if (isBundleBotComponent(type)) {
      const htmlTag = getBundleBotHtmlEquivalent(type);
      if (htmlTag === 'b' || htmlTag === 'a') {
        return handleFormattingTag(type, props.children);
      }
    }

    // Handle regular HTML formatting tags
    if (type === 'b' || type === 'a') {
      return handleFormattingTag(type, props.children);
    }

    return extractTextContent(props.children);
  }

  return String(child);
};

/**
 * Processes children nodes while preserving formatting for <b> and <a> tags
 */
const processChildrenWithFormatting = (children: ReactNode): string => {
  if (isNullish(children)) return '';

  const primitiveContent = primitiveToString(children);
  if (primitiveContent) return primitiveContent;

  if (Array.isArray(children)) {
    return children.map(processChildNode).join('');
  }

  return processChildNode(children);
};

/**
 * Parses a ReactNode and converts it to an array of strings compatible with AnimatedText component
 *
 * Handles:
 * - h4 elements and BundleBotHeading: extracts text and wraps in <h4> tags
 * - p elements and BundleBotText: flattens nested content while preserving <b> and <a> tags
 * - b elements and BundleBotBold: wraps text in <b> tags
 * - a elements and BundleBotLink: wraps text in <a> tags
 */
export const reactNodeParser = (node: ReactNode): string[] => {
  const result: string[] = [];

  const processTopLevelNode = (currentNode: ReactNode): void => {
    if (isNullish(currentNode)) return;

    if (Array.isArray(currentNode)) {
      currentNode.forEach(processTopLevelNode);
      return;
    }

    const primitiveContent = primitiveToString(currentNode);
    if (primitiveContent.trim()) {
      result.push(primitiveContent.trim());
      return;
    }

    if (isReactElement(currentNode)) {
      const { type, props } = currentNode;

      // Handle BundleBot components first
      if (isBundleBotComponent(type)) {
        const htmlTag = getBundleBotHtmlEquivalent(type);

        if (htmlTag === 'h4') {
          const headingContent = extractTextContent(props.children);
          if (headingContent) {
            result.push(`<h4>${headingContent}</h4>`);
          }
          return;
        }

        if (htmlTag === 'p') {
          const textContent = processChildrenWithFormatting(props.children);
          if (textContent.trim()) {
            result.push(textContent);
          }
          return;
        }
      }

      switch (type) {
        case 'h4':
          const h4Content = extractTextContent(props.children);
          if (h4Content) {
            result.push(`<h4>${h4Content}</h4>`);
          }
          break;

        case 'p':
          const pContent = processChildrenWithFormatting(props.children);
          if (pContent.trim()) {
            result.push(pContent);
          }
          break;

        case 'div':
          const children = Array.isArray(props.children)
            ? props.children
            : [props.children];
          children.forEach(processTopLevelNode);
          break;

        default:
          // Check if this is a React Fragment (Symbol) - should process children
          if (typeof type === 'symbol') {
            const fragmentChildren = Array.isArray(props.children)
              ? props.children
              : [props.children];
            fragmentChildren.forEach(processTopLevelNode);
          } else {
            // For other unknown elements, process as content
            const defaultContent = processChildrenWithFormatting(currentNode);
            if (defaultContent.trim()) result.push(defaultContent);
          }
      }
    }
  };

  processTopLevelNode(node);
  return result;
};
